import { useQuery } from "@tanstack/react-query";
import { useAuth } from "./use-auth";

export interface LifecycleSettlement {
  id: string;
  payerName?: string;
  payeeName?: string;
  amount?: number;
  status: "pending" | "confirmed" | "disputed" | "waived";
  confirmedAt: string | null;
  paymentMethod: string | null;
}

export interface LifecycleUI {
  showPendingBanner: boolean;
  showSettlementBanner: boolean;
  showFinalizeModal: boolean;
  showPayoutScreen: boolean;
  isComplete: boolean;
}

export interface RoundLifecycleData {
  roundId: string;
  status: "in_progress" | "pending_finalization" | "finalized" | "settlement_pending" | "settlement_complete" | "abandoned";
  isFinalized: boolean;
  pendingFinalizationAt: string | null;
  finalizedAt: string | null;
  settlementCompletedAt: string | null;
  flags: Record<string, unknown> | null;
  settlements: LifecycleSettlement[];
  ui: LifecycleUI;
}

export function useRoundLifecycle(roundId?: string) {
  const { data, isLoading, error } = useQuery<RoundLifecycleData>({
    queryKey: ["/api/rounds", roundId, "lifecycle"],
    queryFn: async () => {
      const res = await fetch(`/api/rounds/${roundId}/lifecycle`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    enabled: !!roundId,
    staleTime: 1000 * 30,
  });

  return {
    lifecycle: data ?? null,
    isLoading: !!roundId && isLoading,
    error,
  };
}

export interface DashboardPendingRound {
  id: string;
  courseName: string;
  createdAt?: string;
  pendingFinalizationAt?: string | null;
  finalizedAt?: string | null;
}

export interface DashboardPendingData {
  pendingFinalization: DashboardPendingRound[];
  settlementPending: DashboardPendingRound[];
  hasItems: boolean;
}

export function useDashboardPending() {
  const { isAuthenticated } = useAuth();

  const { data, isLoading, error } = useQuery<DashboardPendingData>({
    queryKey: ["/api/dashboard/pending"],
    queryFn: async () => {
      const res = await fetch("/api/dashboard/pending", {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    enabled: isAuthenticated,
    staleTime: 1000 * 60,
  });

  return {
    dashboard: data ?? null,
    isLoading: isAuthenticated && isLoading,
    error,
  };
}

export interface HeadToHeadRecord {
  opponentPersonId: string;
  opponentName: string;
  netAmount: number;
  roundsPlayed: number;
  wins: number;
  losses: number;
}

export interface StrokePerformanceBucket {
  wins: number;
  ties: number;
  losses: number;
  total: number;
  winRate: number;
  countedScores: number;
  countedScoreRate: number;
}

export interface PlayerStatsData {
  personId: string;
  displayName: string;
  groupId?: string;
  lifetimeNet: number;
  roundsPlayed: number;
  pressWins: number;
  pressLosses: number;
  pressWinRate: number;
  biggestComeback: number;
  worstLoss: number;
  bestWin: number;
  headToHead: HeadToHeadRecord[];
  stakesTier: string;
  totalVolume: number;
  roi: number;
  prestigeScore: number;
  styleTag: string;
  strokePerformance?: {
    strokeHoles: StrokePerformanceBucket;
    straightHoles: StrokePerformanceBucket;
    isTeamFormat: boolean;
  } | null;
}

export function usePlayerStats(personId?: string | null) {
  const { data, isLoading, error } = useQuery<PlayerStatsData>({
    queryKey: ["/api/ledger/player", personId],
    queryFn: async () => {
      const res = await fetch(`/api/ledger/player/${personId}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    enabled: !!personId,
    staleTime: 1000 * 60 * 2,
  });

  return {
    stats: data ?? null,
    isLoading: !!personId && isLoading,
    error,
  };
}
